import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, ShieldCheck, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const DEVICE_KEY = 'rarifex_admin_device_id';
const TRUST_KEY = 'rarifex_admin_trust';

const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
};

export const AdminTwoFactorGate = ({ children }) => {
  const [status, setStatus] = useState('checking'); // checking | locked | unlocked | error
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const autoSent = useRef(false);

  const authHeader = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : null;
  }, []);

  const checkTrust = useCallback(async () => {
    setStatus('checking');
    const h = await authHeader();
    if (!h) { setStatus('unlocked'); return; } // non loggato: gestisce la dashboard/route
    try {
      const res = await fetch(`${API}/api/admin/2fa/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({ trust_token: localStorage.getItem(TRUST_KEY) || '', device_id: getDeviceId() }),
      });
      if (res.status === 403) { setStatus('unlocked'); return; } // non superadmin: lascia gestire alla dashboard
      if (res.status === 404) { setStatus('unlocked'); return; } // 2FA non attiva su questo backend: non bloccare l'accesso
      if (!res.ok) { setStatus('error'); return; }
      const d = await res.json();
      setStatus(d.trusted ? 'unlocked' : 'locked');
    } catch (e) {
      setStatus('error');
    }
  }, [authHeader]);

  useEffect(() => { checkTrust(); }, [checkTrust]);

  const sendCode = useCallback(async () => {
    setSending(true);
    const h = await authHeader();
    try {
      const res = await fetch(`${API}/api/admin/2fa/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Errore nell\'invio del codice');
      setSentTo(d.to);
      toast.success(`Codice inviato a ${d.to}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  }, [authHeader]);

  // Invio automatico del primo codice quando l'area si blocca
  useEffect(() => {
    if (status === 'locked' && !autoSent.current) {
      autoSent.current = true;
      sendCode();
    }
  }, [status, sendCode]);

  const verify = async () => {
    if (code.trim().length !== 6) { toast.error('Inserisci il codice a 6 cifre'); return; }
    setVerifying(true);
    const h = await authHeader();
    try {
      const res = await fetch(`${API}/api/admin/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({ code: code.trim(), device_id: getDeviceId() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Codice errato');
      localStorage.setItem(TRUST_KEY, d.trust_token);
      toast.success('Accesso verificato');
      setStatus('unlocked');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setVerifying(false);
    }
  };

  if (status === 'unlocked') return children;

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="admin-2fa-checking">
        <Loader2 className="w-8 h-8 animate-spin text-[#008080]" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center max-w-sm" data-testid="admin-2fa-error">
          <p className="text-slate-600 mb-4">Impossibile verificare l'accesso. Riprova.</p>
          <Button onClick={checkTrust} className="bg-[#008080] hover:bg-[#006666]" data-testid="admin-2fa-retry">
            <RefreshCw className="w-4 h-4 mr-2" /> Riprova
          </Button>
        </div>
      </div>
    );
  }

  // locked -> OTP gate
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="admin-2fa-gate">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
        <div className="w-14 h-14 rounded-full bg-[#008080]/10 flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="w-7 h-7 text-[#008080]" />
        </div>
        <h1 className="text-2xl font-bold text-slate-800 text-center">Verifica accesso Superadmin</h1>
        <p className="text-slate-500 text-sm text-center mt-2 mb-6">
          {sentTo
            ? <>Abbiamo inviato un codice a 6 cifre a <strong>{sentTo}</strong>. Inseriscilo per continuare.</>
            : 'Invio del codice di verifica in corso…'}
        </p>

        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          placeholder="______"
          className="text-center text-2xl tracking-[0.5em] font-semibold h-14 mb-4"
          data-testid="admin-2fa-code-input"
          onKeyDown={(e) => { if (e.key === 'Enter') verify(); }}
        />

        <Button
          onClick={verify}
          disabled={verifying || code.length !== 6}
          className="w-full bg-[#008080] hover:bg-[#006666] h-11"
          data-testid="admin-2fa-verify-button"
        >
          {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
          Conferma accesso
        </Button>

        <button
          onClick={sendCode}
          disabled={sending}
          className="w-full mt-4 text-sm text-slate-500 hover:text-[#008080] flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="admin-2fa-resend-button"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
          Invia di nuovo il codice
        </button>
      </div>
    </div>
  );
};

export default AdminTwoFactorGate;
